/*
 * Copyright 2026 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/kmp-project-template/blob/main/LICENSE
 */
@file:OptIn(io.github.mobilebytelabs.worker.ExperimentalWorkerApi::class)

package kpt.sync.infra

import io.github.mobilebytelabs.worker.CoroutineWorker
import io.github.mobilebytelabs.worker.WorkResult
import io.github.mobilebytelabs.worker.WorkerContext
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kpt.core.base.data.infra.Synchronizer
import kpt.core.base.datastore.infra.ChangeListVersions
import kpt.core.base.datastore.infra.SyncStatePersister
import kpt.core.data.currency.CurrencyRepository
import kpt.core.data.economic.MacroIndicatorsRepository

/**
 * Single data-sync worker. Implements [Synchronizer] so its two [Syncable]
 * collaborators ([CurrencyRepository], [MacroIndicatorsRepository]) can read +
 * write [ChangeListVersions] through `this` without an extra abstraction.
 *
 * **No `getAll<Syncable>()`** — the two repos are constructor-injected as named interfaces. Adding a third
 * Syncable (e.g. FRED interest rates) requires editing this class signature +
 * the [SyncModule] binding — not a runtime discovery.
 *
 * The `awaitAll` shape means partial failure is full failure — if one repo
 * throws, the entire sync is `Result.retry()`. This matches NiA's surface
 * (single `Flow<Boolean>` to observers) and is what the template's home
 * dashboard expects.
 */
public class DataSyncWorker(
    context: WorkerContext,
    // demo:begin
    private val currencyRepository: CurrencyRepository,
    private val macroIndicatorsRepository: MacroIndicatorsRepository,
    // demo:end
    private val persister: SyncStatePersister,
) : CoroutineWorker(context), Synchronizer {

    // Synchronizer's in-memory state for THIS worker invocation. Read at start
    // from the persister; written back at end.
    private var workingVersions: ChangeListVersions = ChangeListVersions()

    override suspend fun getChangeListVersions(): ChangeListVersions = workingVersions

    override suspend fun updateChangeListVersions(update: ChangeListVersions.() -> ChangeListVersions) {
        workingVersions = workingVersions.update()
    }

    override suspend fun doWork(): WorkResult {
        workingVersions = persister.read()
        // demo:begin
        val ok = runCatching {
            coroutineScope {
                listOf(
                    async { currencyRepository.syncWith(this@DataSyncWorker) },
                    async { macroIndicatorsRepository.syncWith(this@DataSyncWorker) },
                ).awaitAll().all { it }
            }
        }.getOrDefault(false)
        if (!ok) return WorkResult.retry()
        // demo:end
        persister.write(workingVersions)
        return WorkResult.success()
    }
}
